import { FC, useState } from "react";
import { useAppSelector } from "../../../store";
import EditText from "../../UI/Icons/EditText";
import Warning from "../../UI/Icons/Warning";
import WarningNew from "../../UI/Icons/WarningNew";
import PopupModal from "../../UI/Popup";
import EditNextOfKin from "./edit-forms/EdiitNextOfKin";
import EditBankAccount from "./edit-forms/EditBankAccount";
import EditJobPreferences from "./edit-forms/EditJobPreferences";
import CompletedPersonalDetail from "./edit-forms/EditPersonalDetail";
import EditQualifications from "./edit-forms/EditQualifications";
import MakeARequest from "./MakeARequest";
import BankAccount from "./steps/BankAccount";
import ComplainceCheck from "./steps/ComplianceCheck";
import JobPreferences from "./steps/JobPreferences";
import NextOfKin from "./steps/NextOfKin";
import PersonalDetails from "./steps/PersonalDetails";
import Qualifications from "./steps/Qualifications";

// Define steps that are allowed to be navigated and edited
const availableSteps = [
  "personalDetails",
  "qualifications",
  "complianceCheck",
  "jobPreferences",
  "bankAccount",
  "nextOfKin",
];

const CompletedProfile: FC = () => {
  const userpersonaldetails = useAppSelector(
    (state) => state.onboarding.userpersonaldetails
  );
  const documentsGoingToExpire = useAppSelector(
    (state) => state.onboarding.userpersonaldetails?.documents_expire_one_month
  );
  const documentsExpired = useAppSelector(
    (state) => state.onboarding.userpersonaldetails?.is_documents_expired
  );
  const expiredDocuments = useAppSelector(
    (state) => state.onboarding.userpersonaldetails?.documents_expire_one_month_documents
  );

  const hasComplianceDocsExpired = expiredDocuments?.some((doc) =>
    ["police_check", "working_with_children_check"].includes(doc.document_type)
  );

  const isPendingRequest = userpersonaldetails?.is_pending_request;

  // State to track which step is currently active
  const [currentStep, setCurrentStep] = useState<string>("personalDetails");
  // Controls visibility of edit popup modal
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  // Controls visibility of request popup modal
  const [showRequestModal, setShowRequestModal] = useState<boolean>(false);

  // Opens the edit modal only if the current step is in availableSteps
  const handleEdit = () => {
    if (availableSteps.includes(currentStep)) {
      setShowEditModal(true);
    }
  };

  // Opens the request modal (used to request editing locked sections)
  const handleRequest = (currentStep: string) => {
    setShowRequestModal(true);
  };

  // Closes both modals
  const onClickClose = () => {
    setShowEditModal(false);
    setShowRequestModal(false);
  };

  // Define the full list of profile steps with their respective components
  const steps = [
    {
      label: "Personal Details",
      key: "personalDetails",
      requestFrom: "personal_details",
      viewComponent: <PersonalDetails />,
      editComponent: <CompletedPersonalDetail handleClose={setShowEditModal} />,
    },
    {
      label: "Qualifications",
      key: "qualifications",
      requestFrom: "qualifications",
      viewComponent: <Qualifications />,
      editComponent: (
        <EditQualifications
          handleClose={setShowEditModal}
          setShowRequestModal={setShowRequestModal}
        />
      ),
    },
    ...(hasComplianceDocsExpired ? [
        {
          label: "Compliance Check",
          key: "complianceCheck",
          requestFrom: "compliance_check",
          viewComponent: <ComplainceCheck />,
          editComponent: null,
        },
      ]
    : []),
    {
      label: "Job Preferences",
      key: "jobPreferences",
      viewComponent: <JobPreferences />,
      editComponent: <EditJobPreferences handleClose={setShowEditModal} />,
    },
    {
      label: "Bank Account",
      key: "bankAccount",
      viewComponent: <BankAccount />,
      editComponent: <EditBankAccount handleClose={setShowEditModal} />,
    },
    {
      label: "Next of Kin",
      key: "nextOfKin",
      viewComponent: <NextOfKin />,
      editComponent: <EditNextOfKin handleClose={setShowEditModal} />,
    },
  ];

  return (
    <>
      <div className="d-flex flex-column flex-lg-row gap-50">
        <div className="onbaording-steps min-w-165px d-flex flex-column gap-20 fs-16 fw-400 text-black">
          {steps.map((step) => (
            <div
              key={step.key}
              className={`d-flex align-items-center gap-8 cursor-pointer ${
                currentStep === step.key ? "fw-700" : ""
              }`}
              // Allow navigation only if step is available
              onClick={() => {
                if (availableSteps.includes(step.key)) {
                  setCurrentStep(step.key);
                }
              }}
            >
              {step.label}
              {(documentsGoingToExpire || documentsExpired) ? 
              (step.key === "qualifications" || step.key === "complianceCheck") && (
                <span className="step-icon"><WarningNew /></span>
              ) : "" }
            </div>
          ))}
        </div>
        <div className="border-left border-off-white pl-50px w-100 mobile-p-0 mobile-border-0">
          <div className="d-flex flex-column gap-20 max-w-650px fw-400 text-black">
            <div className="fs-30">
              {steps.find((step) => step.key === currentStep)?.label}
            </div>
            {/* Instruction bar with edit/request options */}
            { steps.find((step) => step.key === currentStep)?.editComponent !== null ? 
              <div className="d-flex flex-column gap-20">
                <div className="bg-off-white px-15px py-10px radius-8px d-flex gap-6 align-items-center align-self-start">
                  <EditText />
                  <div className="fs-13">
                    {currentStep === "nextOfKin" || currentStep === "bankAccount"
                      ? "The below details "
                      : "Selected details "}
                    <span
                      className="fw-700 text-decoration-underline cursor-pointer"
                      onClick={() => handleEdit()}
                    >
                      may be edited here.
                    </span>
                    {/* Conditionally show this sentence only for specific steps */}
                    {[
                      "personalDetails",
                      "qualifications",
                      "jobPreferences",
                    ].includes(currentStep) && !isPendingRequest ? (
                      <>
                        {" "}
                        To modify any locked details please{" "}
                        <span
                          className="fw-700 text-decoration-underline cursor-pointer"
                          onClick={() => handleRequest(currentStep)}
                        >
                          make a request.
                        </span>
                      </>
                    ) :  null}
                  </div>
                </div>

              {
                isPendingRequest && currentStep !== "nextOfKin" && currentStep !== "bankAccount" && (
                  <div className="bg-teal-light px-15px py-10px radius-8px d-flex gap-6 align-self-start align-items-center max-w-600px">
                    <Warning />
                    <div className="fs-13 fw-400 text-black">
                      You've submitted a request to <strong>modify locked details</strong>. Please allow up to 1 business day 
                      for one of our consultants to review and approve changes.
                    </div>
                  </div>
                )
              }
                
              </div>
            : "" }
            {steps.find((step) => step.key === currentStep)?.viewComponent}
          </div>
        </div>
      </div>
      <PopupModal
        visible={showEditModal}
        showCloseBtn={true}
        onClickClose={onClickClose}
        customWidth={650}
      >
        {steps.find((step) => step.key === currentStep)?.editComponent}
      </PopupModal>
      <MakeARequest
        showRequestModal={showRequestModal}
        setShowRequestModal={setShowRequestModal}
        reuestFrom={currentStep}
      />
    </>
  );
};

export default CompletedProfile;
